// src/components/Context/UserContext.tsx
/* eslint-disable no-console */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

// 🔹 Amplify Data client & types
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
type Profile = Schema['Profile']['type'];

interface UserContextType {
  isAuthenticated: boolean;
  isGuest: boolean;            // stays false without Identity Pool
  identityId: string | null;   // stays null without Identity Pool
  sub: string | null;          // Cognito sub
  userId: string | null;       // 🔹 alias of sub for app code
  firstName: string | null;
  email: string | null;
  setFirstName: (name: string | null) => void;
  setEmail: (email: string | null) => void;
  refreshUser: () => Promise<void>;
  clearUser: () => void;
  __uid: number;
}

const UserContext = createContext<UserContextType>({
  isAuthenticated: false,
  isGuest: false,
  identityId: null,
  sub: null,
  userId: null,
  firstName: null,
  email: null,
  setFirstName: () => {},
  setEmail: () => {},
  refreshUser: async () => {},
  clearUser: () => {},
  __uid: 0,
});

// ────────────────────────────────────────────────────────────────────────────
// helpers
// ────────────────────────────────────────────────────────────────────────────
function toEmailLower(email: string | null | undefined): string | null {
  if (!email) return null;
  return email.trim().toLowerCase();
}

// ────────────────────────────────────────────────────────────────────────────
// Create or update the user's Profile once they are authenticated.
// PK = emailLower (unique). Also store sub + extra.firstName.
// ────────────────────────────────────────────────────────────────────────────
async function upsertProfile(params: {
  sub: string | null;
  email: string | null;
  firstName: string | null;
}): Promise<Profile | null> {
  const { sub, email, firstName } = params;

  // Try to determine the PK we should use
  let emailLower = toEmailLower(email);

  try {
    let existing: Profile | null = null;

    if (!emailLower && sub) {
      // If we don't yet have email, try to find an existing profile by sub
      const listed = await client.models.Profile.list({
        filter: { sub: { eq: sub } },
        limit: 1,
      });
      const found = (listed as any)?.data?.[0] as Profile | undefined;
      if (found) {
        existing = found;
        emailLower = (found as any).emailLower as string | null;
      }
    }

    // If we now have emailLower, try a direct PK get
    if (emailLower) {
      const got = await client.models.Profile.get({ emailLower });
      const byPk = (got as any)?.data as Profile | null;
      if (byPk) existing = byPk;
    }

    // If still no emailLower and no existing profile, we cannot create (PK required)
    if (!existing && !emailLower) {
      console.warn(
        '[UserContext] upsertProfile: cannot create Profile — missing emailLower (no email yet).'
      );
      return null;
    }

    // Create if not existing
    if (!existing && emailLower) {
      const created = await client.models.Profile.create({
        emailLower,
        email: email ?? undefined,
        sub: sub ?? undefined,
        // Prefer using flexible JSON if firstName isn't a top-level field in your schema
        extra: firstName ? { firstName } : undefined,
      } as any);
      return (created as any)?.data ?? null;
    }

    // Update if values changed
    if (existing) {
      const next: any = { emailLower: (existing as any).emailLower };

      // Keep canonical emailLower as-is; update other fields only if changed
      const wantEmail = email ?? (existing as any).email ?? undefined;
      const wantSub = sub ?? (existing as any).sub ?? undefined;

      let needUpdate = false;

      if ((existing as any).email !== wantEmail) {
        next.email = wantEmail;
        needUpdate = true;
      }

      if ((existing as any).sub !== wantSub) {
        next.sub = wantSub;
        needUpdate = true;
      }

      // Merge extra.firstName
      const prevExtra = ((existing as any).extra ?? {}) as Record<string, any>;
      const wantExtra =
        firstName ? { ...prevExtra, firstName } : Object.keys(prevExtra).length ? prevExtra : undefined;

      // Only mark update if extra actually changed
      const prevFirst = prevExtra?.firstName ?? undefined;
      if (wantExtra && prevFirst !== wantExtra.firstName) {
        next.extra = wantExtra;
        needUpdate = true;
      }

      if (needUpdate) {
        const updated = await client.models.Profile.update(next);
        return (updated as any)?.data ?? existing;
      }

      return existing;
    }

    return null;
  } catch (err) {
    console.error('[UserContext] upsertProfile failed:', err);
    return null;
  }
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [identityId, setIdentityId] = useState<string | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [uid, setUid] = useState(0);

  // prevent overlapping refreshes
  const refreshing = useRef(false);

  const clearUser = useCallback(() => {
    setIsAuthenticated(false);
    setIsGuest(false);
    setIdentityId(null);
    setSub(null);
    setFirstName(null);
    setEmail(null);
    setUid((n) => n + 1);
  }, []);

  const refreshUser = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;

    try {
      // Throws if not signed in
      const user = await getCurrentUser();

      // Signed-in path (User Pool only)
      setIsAuthenticated(true);
      setIsGuest(false);         // no Identity Pool => never guest
      setIdentityId(null);       // no Identity Pool => always null

      const theSub = user?.userId ?? null;
      setSub(theSub);

      // Attributes may not exist for all fields; keep sensible fallbacks
      let attrs: Record<string, string> = {};
      try {
        attrs = (await fetchUserAttributes()) as Record<string, string>;
      } catch {
        // ignore; we'll fall back to signInDetails when possible
      }

      const emailFromAttrs =
        attrs.email ||
        (user as any)?.signInDetails?.loginId ||
        null;

      const firstFromAttrs =
        attrs.given_name ||
        attrs.name ||
        (attrs as any)['custom:firstName'] ||
        null;

      setEmail(emailFromAttrs ?? null);
      setFirstName(firstFromAttrs ?? null);

      // 🔹 Ensure Profile exists/updated for this user (uses emailLower PK)
      await upsertProfile({
        sub: theSub,
        email: emailFromAttrs,
        firstName: firstFromAttrs,
      });

      setUid((n) => n + 1);
    } catch {
      // Signed-out path
      clearUser();
    } finally {
      refreshing.current = false;
    }
  }, [clearUser]);

  useEffect(() => {
    void refreshUser();

    // Refresh on any auth event
    const unsub = Hub.listen('auth', () => {
      if (!refreshing.current) void refreshUser();
    });

    return () => unsub();
  }, [refreshUser]);

  return (
    <UserContext.Provider
      value={{
        isAuthenticated,
        isGuest,
        identityId,
        sub,
        userId: sub,         // alias
        firstName,
        email,
        setFirstName,
        setEmail,
        refreshUser,
        clearUser,
        __uid: uid,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
